import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Send, RefreshCw, ChevronDown, ChevronRight, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/formatters';
import { useOfferSend, SendOfferDialog, InviteeTable, STATUS_COLORS } from './offerSendKit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 10/23 (Josh): offers that are out for confirmation — per deal who was emailed, who answered, pending adjustment
 * requests — with "Send to…" (pick investors; existing invitees pre-checked) and "Resend to unanswered". Both call
 * POST /syndication-offer-email/send, which reuses each invitee's personal link, so a resend never changes the URL.
 */
export default function OffersInFlight({ onChanged }) {
  const { getAuthHeaders } = useAuth();
  const headers = useCallback(() => ({ ...getAuthHeaders(), 'X-Tenant': 'live' }), [getAuthHeaders]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/syndications-outstanding/offers`, { headers: headers() });
      setRows(r.data?.offers || []);
    } catch (e) {
      toast.error(`Offers in flight: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);
  // 10/27: send / resend / invitee table are shared with the Needs Syndication page (offerSendKit)
  const { busyKey, send, openSendDialog, resendUnanswered, dialogProps } = useOfferSend({ onDone: async () => { await load(); onChanged && onChanged(); } });

  const totals = useMemo(() => ({
    deals: rows.length, unanswered: rows.reduce((s, r) => s + r.unanswered, 0), adj: rows.reduce((s, r) => s + r.pending_adjustments, 0),
  }), [rows]);

  return (
    <Card id="offers-in-flight" data-testid="offers-in-flight">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2"><Mail className="h-4 w-4 text-blue-600" /> Offers out for confirmation</CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {totals.deals} deal{totals.deals === 1 ? '' : 's'} · {totals.unanswered} invitee{totals.unanswered === 1 ? '' : 's'} yet to answer · {totals.adj} adjustment request{totals.adj === 1 ? '' : 's'} to approve.
              Resending keeps each syndicator&apos;s personal link.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : rows.length === 0 ? (
          <p className="text-center text-slate-400 py-10">No offers are out right now. Deals that still need syndication are on <Link className="underline" to="/syndications/unsyndicated">Needs Syndication</Link>.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Deal</TableHead><TableHead>Merchant</TableHead><TableHead className="text-right">Funding</TableHead>
                <TableHead>Offer</TableHead><TableHead className="text-right">Invited</TableHead><TableHead className="text-right">Answered</TableHead>
                <TableHead className="text-right">Waiting</TableHead><TableHead className="text-right">Adjustments</TableHead><TableHead className="text-right w-72">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <React.Fragment key={row.offer_id}>
                  <TableRow className={row.ready_to_confirm ? 'bg-emerald-50/40' : row.pending_adjustments ? 'bg-amber-50/40' : ''}>
                    <TableCell>
                      <button type="button" className="text-slate-500" onClick={() => setOpen((o) => ({ ...o, [row.offer_id]: !o[row.offer_id] }))} aria-label="toggle invitees">
                        {open[row.offer_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </TableCell>
                    <TableCell className="font-medium"><Link className="text-blue-600 hover:underline" to={`/deals/${row.deal_id}/offering`}>#{row.deal_id_number}</Link></TableCell>
                    <TableCell className="text-slate-600 max-w-[200px] truncate">{row.deal_name}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.funding_amount)}</TableCell>
                    <TableCell><Badge className={STATUS_COLORS[row.offer_status] || ''}>{row.offer_status}</Badge>{row.ready_to_confirm && <span className="ml-1 text-xs text-emerald-700">ready</span>}</TableCell>
                    <TableCell className="text-right">{row.invited}</TableCell>
                    <TableCell className="text-right">{row.answered}</TableCell>
                    <TableCell className="text-right">{row.unanswered ? <span className="text-blue-700 font-semibold">{row.unanswered}</span> : '—'}{row.never_emailed ? <span className="text-xs text-red-600 ml-1" title="invited but no email was ever sent">({row.never_emailed} never emailed)</span> : null}</TableCell>
                    <TableCell className="text-right">{row.pending_adjustments ? <span className="text-amber-700 font-semibold">{row.pending_adjustments}</span> : '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <Button size="sm" variant="outline" disabled={busyKey === row.deal_id} onClick={() => openSendDialog(row)}><Send className="h-3.5 w-3.5 mr-1" /> Send to…</Button>
                        <Button size="sm" variant="outline" disabled={busyKey === row.deal_id || !row.unanswered} onClick={() => resendUnanswered(row)} title="Email again everyone who has not answered"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Resend ({row.unanswered})</Button>
                        {row.pending_adjustments > 0 && <Link to={`/deals/${row.deal_id}/offering`}><Button size="sm">Review</Button></Link>}
                        {row.ready_to_confirm && <Link to={`/deals/${row.deal_id}/offering`}><Button size="sm">Confirm</Button></Link>}
                      </div>
                    </TableCell>
                  </TableRow>
                  {open[row.offer_id] && (
                    <TableRow>
                      <TableCell colSpan={10} className="bg-slate-50 p-0">
                        <InviteeTable row={row} busyKey={busyKey} send={send} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <SendOfferDialog {...dialogProps} />
    </Card>
  );
}
